import React from 'react';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import EditIcon from 'assets/images/edit-icon.svg';
import Grid from '@material-ui/core/Grid';
import classnames from 'classnames';
import styles from './styles';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { addLeadingZero, getEffectiveStartTime } from 'helpers/helpers';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { convertTime } from 'common/dateFormatConfig';
import { isMobileSchedule } from 'helpers/_helpers';
import useWorklogStyles from 'components/WorklogDetails/hooks/useWorklogStyles';
import { useGetRemovedHistoryItem } from 'components/WorklogDetails/hooks/useGetRemovedHistoryItem';

const TimeBlock = ({
  classes,
  getFormattedHoursRange,
  hasAccess,
  openAdditionalDrawer,
  hours,
  sheet,
  historyState,
}) => {
  const worklogClasses = useWorklogStyles();
  const getTimeFormatHook = useTimeSettingsFormat();

  const startTime = sheet.startTime;
  const startTimeDate = sheet.startTimeDate;
  const hoursRange = sheet.hours;

  const displayStartTime = getEffectiveStartTime(startTime, startTimeDate);
  const formattedStartTime = convertTime(displayStartTime, getTimeFormatHook().is12Format);

  const hoursDiff = historyState?.diff?.hours;
  const { removed: isHoursStartRemoved, value: prevHoursStart } = useGetRemovedHistoryItem(hoursDiff?.start);
  const { removed: isHoursEndRemoved, value: prevHoursEnd } = useGetRemovedHistoryItem(hoursDiff?.end);
  const hoursStartAdded = !prevHoursStart && !!sheet.hours?.start;
  const hoursEndAdded = !prevHoursEnd && !!sheet.hours?.end;

  const hasOnJobTimeChanged = historyState &&
    (hoursDiff?.start?.length || hoursDiff?.end?.length) &&
    ((hoursStartAdded ||
      ((!!prevHoursStart === !!sheet.hours.start &&
      new Date(prevHoursStart)?.getTime() !== new Date(sheet.hours.start)?.getTime()))
    ) || (
      hoursEndAdded &&
      !!prevHoursEnd === !!sheet.hours.end &&
      new Date(prevHoursEnd)?.getTime() !== new Date(sheet.hours.end)?.getTime()
    ))
  const hasOnJobTimeRemoved = historyState && isHoursStartRemoved && isHoursEndRemoved;
  const hasTravelTimeOnlyChanged = !!historyState?.diff?.travelTimeOnly?.length;

  const renderOnJobTime = () => (
    <Grid item xs={12} md={3}>
      <Typography
        variant="body1"
        color="textSecondary"
        className={classnames(classes.regularFontWeight, classes.paddingBottom)}
      >
        On Job Time:
      </Typography>
      <Typography
        variant="body1"
        color="textPrimary"
        className={classnames(
          hasOnJobTimeChanged ? worklogClasses.showDiff : null,
          hasOnJobTimeRemoved ? worklogClasses.showDiffRemoved : null,
        )}
      >
        {getFormattedHoursRange(hasOnJobTimeRemoved
          ? ({ start: new Date(prevHoursStart), end: new Date(prevHoursEnd) })
          : hoursRange
        )}
      </Typography>
    </Grid>
  )

  const startTimeDiff = historyState?.diff?.startTime;
  const { removed: isStartTimeHourRemoved, value: prevStartTimeHour } = useGetRemovedHistoryItem(historyState?.diff?.startTime?.hour);
  const { removed: isStartTimeMinuteRemoved, value: prevStartTimeMinute } = useGetRemovedHistoryItem(historyState?.diff?.startTime?.minute);
  const hasStartTimeChanged = historyState &&
    (startTimeDiff?.hour?.length || startTimeDiff?.minute?.length) &&
    (prevStartTimeHour !== startTime?.hour || prevStartTimeMinute !== startTime?.minute)
  const startTimeAdded = hasStartTimeChanged && !prevStartTimeHour && !prevStartTimeMinute;
  const hasStartTimeRemoved = isStartTimeHourRemoved && isStartTimeMinuteRemoved;
  const removedStartTime = { hour: prevStartTimeHour, minute: prevStartTimeMinute }

  const renderStartTime = () => (
    <Grid item xs={12} md={2}>
      <div className={classnames(classes.header, classes.paddingBottom)}>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classnames(classes.regularFontWeight)}
        >
          Start Time:
        </Typography>
        {hasAccess && (
          <Button
            className={classes.editButton}
            variant="text"
            onClick={openAdditionalDrawer('time')}
            disableRipple
            disableTouchRipple
          >
            <img src={EditIcon} alt="edit time" />
          </Button>
        )}
      </div>
      <Typography variant="body1" color="textPrimary">
        <span
          className={classnames(
            (hasStartTimeChanged && !hasStartTimeRemoved) ? worklogClasses.showDiff : null,
            hasStartTimeRemoved ? worklogClasses.showDiffRemoved : null,
          )}
        >
          {hasStartTimeRemoved
            ? convertTime(removedStartTime,getTimeFormatHook().is12Format)
            : formattedStartTime}
        </span>
      </Typography>
    </Grid>
  )

  const travelTimeToDiff = historyState?.diff?.hours?.travelTimeTo;
  const travelTimeFromDiff = historyState?.diff?.hours?.travelTimeFrom;
  const rainPayDiff = historyState?.diff?.rainPay;
  const { value: prevTravelTimeToHour } = useGetRemovedHistoryItem(travelTimeToDiff?.hours);
  const { value: prevTravelTimeToMinute } = useGetRemovedHistoryItem(travelTimeToDiff?.minutes);
  const hasTravelTimeToChanged = historyState &&
    (travelTimeToDiff?.hours?.length || travelTimeToDiff?.minutes?.length) &&
    (
      prevTravelTimeToHour !== hours.travelTimeTo?.hours ||
      prevTravelTimeToMinute !== hours.travelTimeTo?.minute
    )

  const { value: prevTravelTimeFromHour } = useGetRemovedHistoryItem(travelTimeFromDiff?.hours);
  const { value: prevTravelTimeFromMinute } = useGetRemovedHistoryItem(travelTimeFromDiff?.minutes);
  const hasTravelTimeFromChanged = historyState &&
    (travelTimeFromDiff?.hours?.length || travelTimeFromDiff?.minutes?.length) &&
    (
      prevTravelTimeFromHour !== hours.travelTimeFrom?.hours ||
      prevTravelTimeFromMinute !== hours.travelTimeFrom?.minute
    )

  const { value: prevRainPay } = useGetRemovedHistoryItem(historyState?.diff?.isRainPayEnabled);
  const { value: prevRainPayHours } = useGetRemovedHistoryItem(rainPayDiff?.hours);
  const { value: prevRainPayMinutes } = useGetRemovedHistoryItem(rainPayDiff?.minutes);
  const hasRainPayChanged = historyState &&
    (
      (historyState?.diff?.isRainPayEnabled?.length && prevRainPay !== sheet.isRainPayEnabled) ||
      (rainPayDiff?.hours?.length && prevRainPayHours !== sheet.rainPay?.hours) ||
      (rainPayDiff?.minutes?.length && prevRainPayMinutes !== sheet.rainPay?.minutes)
    )
  const hasRainPayRemoved = !!historyState && !(+sheet.rainPay?.hours) && !(+sheet.rainPay?.minutes) && (
    (historyState?.diff?.isRainPayEnabled?.length && !sheet.isRainPayEnabled) ||
    (rainPayDiff?.hours?.length || rainPayDiff?.minutes?.length)
  )

  return (
    <>
      {renderStartTime()}
      {isMobileSchedule()
        ? (!sheet.travelTimeOnly ? renderOnJobTime() : null)
        : ((!sheet.travelTimeOnly || hasTravelTimeOnlyChanged) ? renderOnJobTime() : null)
      }
      {sheet?.rainPay &&
        (sheet?.isRainPayEnabled || prevRainPay) &&
        (+sheet?.rainPay.hours > 0 || +sheet?.rainPay.minutes > 0 || prevRainPayHours || prevRainPayMinutes) && (
          <Grid item xs={12} md={2}>
            <Typography
              variant="body1"
              color="textSecondary"
              className={classnames(classes.regularFontWeight, classes.paddingBottom)}
            >
              Misc. Stipend:
            </Typography>
            <Typography
              variant="body1"
              color="textPrimary"
              style={{ display: 'flex', alignItems: 'center' }}
              className={classnames(
                (hasRainPayChanged && !hasRainPayRemoved) ? worklogClasses?.showDiff : null,
                hasRainPayRemoved ? worklogClasses?.showDiffRemoved : null
              )}
            >
              {((+sheet?.rainPay?.hours) || (+sheet?.rainPay?.minutes))
                ? `${addLeadingZero(sheet?.rainPay?.hours)}h ${addLeadingZero(sheet?.rainPay?.minutes)}m`
                : (prevRainPayHours || prevRainPayMinutes)
                  ? `${addLeadingZero(prevRainPayHours)}h ${addLeadingZero(prevRainPayMinutes)}m`
                  : ''
              }
              <Tooltip
                title={sheet?.rainPay?.description || ''}
                placement="top"
                enterTouchDelay={0}
              >
                <InfoIcon
                  fontSize="small"
                  style={{ width: 16, height: 16, marginLeft: 5 }}
                  color="primary"
                />
              </Tooltip>
            </Typography>
          </Grid>
        )}
      <Grid item xs={12} md={3}>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classnames(classes.regularFontWeight, classes.paddingBottom)}
        >
          Travel Time To Job:
        </Typography>
        <Typography variant="body1" color="textPrimary">
          <span className={classnames(hasTravelTimeToChanged ? worklogClasses?.showDiff : null)}>
            {addLeadingZero(hours?.travelTimeTo?.hours)}h{' '}
            {addLeadingZero(hours?.travelTimeTo?.minutes)}m
          </span>
        </Typography>
      </Grid>
      <Grid item xs={12} md={3}>
        <Typography
          variant="body1"
          color="textSecondary"
          className={classnames(classes.regularFontWeight, classes.paddingBottom)}
        >
          Travel Time From Job:
        </Typography>
        <Typography variant="body1" color="textPrimary">
          <span className={classnames(hasTravelTimeFromChanged ? worklogClasses?.showDiff : null)}>
            {addLeadingZero(hours?.travelTimeFrom?.hours)}h{' '}
            {addLeadingZero(hours?.travelTimeFrom?.minutes)}m
          </span>
        </Typography>
      </Grid>
    </>
  );
};

TimeBlock.propTypes = {
  classes: propTypes.object.isRequired,
  getFormattedHoursRange: propTypes.func.isRequired,
  travelTime: propTypes.object,
  startTime: propTypes.object.isRequired,
  hasAccess: propTypes.bool.isRequired,
  openAdditionalDrawer: propTypes.func.isRequired,
};

export default withStyles(styles)(TimeBlock);
